import { Stack, Typography, useTheme, useMediaQuery } from "@mui/material";
import { useState, useEffect, useContext } from "react";
import axios from "axios";
import { DialogContext, DialogType } from "avrora";

interface ILicense {
    id: string;
    validUntil: string;
    issuedAt: string;
    users: number;
    activeUsers: number;
    grantedTo: string;
    validByDate: boolean;
    validByUserCount: boolean;
}

const formatDate = (dateString: string, dateFinish: string): string => {
    const date = new Date(dateString);
    const [datePart] = date.toISOString().split('T');
    const diffInMs = Number(new Date(dateFinish)) - Number(new Date(dateString));
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
    const time = date.toTimeString().slice(0, 5);

    return `${datePart} ${time} - осталось дней ${diffInDays}`;
};

export const License = () => {
    const dialogContext = useContext(DialogContext);
    const [license, setLicense] = useState<ILicense | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    const isMobile = useMediaQuery("(max-width:499px)");

    useEffect(() => {
        setIsLoading(true);
        axios
            .get(`/eus/admin/license`)
            .then((res) => setLicense(res.data))
            .catch((e) => {
                dialogContext.push({
                    text: e.response ? JSON.stringify(e.response.data) : e.toString(),
                    title: "Ошибка",
                    type: DialogType.Error,
                });
                setLicense(null);
            })
            .finally(() => setIsLoading(false));
    }, []);

    if (isLoading) return null;

    if (!license) {
        return (
            <Stack
                sx={{
                    maxWidth: isMobile ? "100%" : "1024px",
                    minWidth: isMobile ? "none" : "1024px",
                    padding: isMobile ? "2rem" : "3rem",
                    width: "100%",
                    boxSizing: "border-box",
                }}
                spacing={isMobile ? 1.5 : 2}
            >
                <Typography variant="h1" fontSize={isMobile ? "1.5rem" : undefined}>
                    Лицензия
                </Typography>
                <Typography>Лицензия недоступна</Typography>
            </Stack>
        );
    }

    return (
        <Stack
            sx={{
                maxWidth: isMobile ? "100%" : "1024px",
                minWidth: isMobile ? "none" : "1024px",
                padding: isMobile ? "2rem" : "3rem",
                width: "100%",
                boxSizing: "border-box",
            }}
            spacing={isMobile ? 1.5 : 2}
        >
            <Typography variant="h1" fontSize={isMobile ? "1.5rem" : undefined}>
                Лицензия
            </Typography>

            <Stack spacing={isMobile ? 1.5 : 2}>
                {[
                    { label: "Название компании:", value: license.grantedTo, width: "170px" },
                    {
                        label: "Количество пользователей:",
                        value: `${license.users} (можно завести еще ${license.users - license.activeUsers})`,
                        width: "225px",
                    },
                    {
                        label: "Дата выдачи:",
                        value: formatDate(license.issuedAt, license.validUntil),
                        width: "110px",
                    },
                    {
                        label: "Дата окончания действия лицензии:",
                        value: license.validUntil,
                        width: "295px",
                    },
                ].map((item, index) => (
                    <Stack
                        key={index}
                        direction={isMobile ? "column" : "row"}
                        spacing={isMobile ? 0.5 : 2}
                        alignItems={isMobile ? "flex-start" : "center"}
                    >
                        <Stack width={isMobile ? "auto" : item.width}>
                            <Typography
                                fontWeight={isMobile ? 500 : undefined}
                                color={isMobile ? "text.secondary" : undefined}
                            >
                                {item.label}
                            </Typography>
                        </Stack>
                        <Typography>{item.value}</Typography>
                    </Stack>
                ))}
            </Stack>
        </Stack>
    );
};
